import { useTranslation } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { MapPin, Star, Clock, Navigation, Info, Headphones, Bookmark } from 'lucide-react';

/**
 * PlaceCard - Unified card component for displaying place information
 * Used in QuickDeck, ScanHere, and Visit Companion
 * Design based on the Musée du Louvre reference card
 */
export function PlaceCard({ 
  place, 
  onNavigate, 
  onInfo, 
  onAudioGuide, 
  onBookmark,
  variant = 'default' // 'default' | 'compact'
}) {
  const { language } = useTranslation();

  if (!place) return null;

  const {
    name,
    category,
    address,
    description,
    rating,
    reviews_count,
    distance,
    duration,
    budget,
    open_now,
    maps_url,
    lat,
    lng,
  } = place;

  // Format rating stars
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star 
          key={i} 
          className={`w-4 h-4 ${i < fullStars ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`}
        />
      );
    }
    return stars;
  };

  // Handle navigation
  const handleNavigate = () => {
    if (onNavigate) {
      onNavigate(place);
    } else {
      const url = maps_url || 
        (lat && lng 
          ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`);
      window.open(url, '_blank');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-5 shadow-lg border border-gray-100"
      data-testid="place-card"
    >
      {/* Header: Name + Open/Closed Badge */}
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-xl font-bold text-gray-900 leading-tight pr-4">{name}</h3>
        {open_now !== undefined && open_now !== null && (
          <span className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${
            open_now 
              ? 'bg-green-100 text-green-700' 
              : 'bg-red-50 text-red-600'
          }`}>
            {open_now 
              ? (language === 'fr' ? 'Ouvert' : 'Open')
              : (language === 'fr' ? 'Fermé' : 'Closed')}
          </span>
        )}
      </div>

      {/* Category Badge */}
      {category && (
        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 uppercase tracking-wide mb-3">
          {category}
        </span>
      )}

      {/* Rating */}
      {rating && (
        <div className="flex items-center gap-2 mb-3">
          <div className="flex">
            {renderStars(rating)}
          </div>
          <span className="text-base font-bold text-violet-700">{rating}</span>
          {reviews_count && (
            <span className="text-sm text-gray-400">({reviews_count.toLocaleString()})</span>
          )}
        </div>
      )}

      {/* Address */}
      {address && (
        <div className="flex items-start gap-2 mb-3">
          <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-gray-600">{address}</span>
        </div>
      )}

      {/* Description */}
      {description && (
        <p className="text-sm text-gray-700 leading-relaxed mb-4 line-clamp-3">
          {description}
        </p>
      )}

      {/* Info Bar: Distance / Duration / Budget */}
      <div className="flex items-center gap-4 text-sm text-gray-500 mb-5">
        {distance && (
          <div className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            <span>{distance}</span>
          </div>
        )}
        {duration && (
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{duration}</span>
          </div>
        )}
        {budget && (
          <span className="font-medium">{budget}</span>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        {/* Primary: Y aller */}
        <button
          onClick={handleNavigate}
          className="flex-1 flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-gradient-to-r from-violet-500 to-violet-600 text-white font-semibold text-sm hover:from-violet-600 hover:to-violet-700 active:scale-[0.98] transition-all shadow-lg shadow-violet-500/30"
          data-testid="place-card-navigate"
        >
          <Navigation className="w-4 h-4" />
          {language === 'fr' ? 'Y aller' : 'Go there'}
        </button>

        {/* Secondary: Info */}
        <button
          onClick={() => onInfo?.(place)}
          className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 flex items-center justify-center hover:border-violet-300 hover:bg-violet-50 active:scale-95 transition-all"
          title={language === 'fr' ? 'Plus d\'infos' : 'More info'}
          data-testid="place-card-info"
        >
          <Info className="w-5 h-5 text-gray-600" />
        </button>

        {/* Secondary: Audio Guide */}
        <button
          onClick={() => onAudioGuide?.(place)}
          className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-50 to-violet-50 border border-violet-200 flex items-center justify-center hover:border-violet-400 hover:bg-violet-100 active:scale-95 transition-all"
          title={language === 'fr' ? 'Audio Guide' : 'Audio Guide'}
          data-testid="place-card-audio"
        >
          <Headphones className="w-5 h-5 text-violet-600" />
        </button>

        {/* Secondary: Bookmark */}
        <button
          onClick={() => onBookmark?.(place)}
          className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 flex items-center justify-center hover:border-violet-300 hover:bg-violet-50 active:scale-95 transition-all"
          title={language === 'fr' ? 'Sauvegarder' : 'Save'}
          data-testid="place-card-bookmark"
        >
          <Bookmark className="w-5 h-5 text-gray-600" />
        </button>
      </div>
    </motion.div>
  );
}

export default PlaceCard;
